import { useId, CSSProperties } from 'react'

interface BunsenBurnerProps {
  active?: boolean
  flameIntensity?: 'low' | 'medium' | 'high'
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const BunsenBurner = ({
  active = false,
  flameIntensity = 'medium',
  onClick,
  disabled = false,
  className = ''
}: BunsenBurnerProps) => {
  const id = useId()

  const flameHeights = {
    low: { outer: 25, inner: 18 },
    medium: { outer: 40, inner: 30 },
    high: { outer: 55, inner: 42 }
  }

  const flameHeight = flameHeights[flameIntensity]

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  const flameAnimationStyle: CSSProperties = {
    animation: 'flicker 0.15s ease-in-out infinite alternate'
  }

  return (
    <svg
      viewBox="0 0 80 140"
      width={70}
      height={125}
      onClick={disabled ? undefined : onClick}
      style={containerStyle}
      className={className}
    >
      <defs>
        {/* Metal gradient for burner body */}
        <linearGradient id={`metal-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#4A4A4A" />
          <stop offset="20%" stopColor="#6A6A6A" />
          <stop offset="50%" stopColor="#8A8A8A" />
          <stop offset="80%" stopColor="#6A6A6A" />
          <stop offset="100%" stopColor="#4A4A4A" />
        </linearGradient>

        {/* Dark metal for base */}
        <linearGradient id={`base-metal-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2C2C2C" />
          <stop offset="30%" stopColor="#4A4A4A" />
          <stop offset="70%" stopColor="#4A4A4A" />
          <stop offset="100%" stopColor="#2C2C2C" />
        </linearGradient>

        {/* Outer blue flame gradient */}
        <linearGradient id={`flame-outer-${id}`} x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#FF5A36" stopOpacity="0.9" />
          <stop offset="30%" stopColor="#1E90FF" stopOpacity="0.85" />
          <stop offset="70%" stopColor="#00BFFF" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#87CEEB" stopOpacity="0" />
        </linearGradient>

        {/* Inner flame gradient - hottest part */}
        <linearGradient id={`flame-inner-${id}`} x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="40%" stopColor="#00CED1" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#00BFFF" stopOpacity="0" />
        </linearGradient>

        {/* Glow filter for flame */}
        <filter id={`flame-glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.3" />
        </filter>
      </defs>

      {/* Keyframe animation for flame flicker */}
      <style>
        {`
          @keyframes flicker {
            0% { transform: scaleX(1) scaleY(1); }
            100% { transform: scaleX(0.95) scaleY(1.02); }
          }
        `}
      </style>

      {/* Active indicator glow */}
      {active && (
        <ellipse
          cx="40"
          cy="60"
          rx="35"
          ry="50"
          fill="none"
          stroke="#FF5A36"
          strokeWidth="2"
          opacity="0.6"
          style={{ animation: 'glow 1.5s ease-in-out infinite' }}
        />
      )}

      <g filter={`url(#shadow-${id})`}>
        {/* Base plate */}
        <ellipse
          cx="40"
          cy="130"
          rx="30"
          ry="6"
          fill={`url(#base-metal-${id})`}
        />

        {/* Base ring */}
        <ellipse
          cx="40"
          cy="128"
          rx="28"
          ry="5"
          fill="#3A3A3A"
          stroke="#5A5A5A"
          strokeWidth="1"
        />

        {/* Tube body - lower section */}
        <rect
          x="32"
          y="85"
          width="16"
          height="45"
          fill={`url(#metal-${id})`}
          rx="2"
        />

        {/* Air intake holes */}
        <ellipse cx="35" cy="110" rx="2" ry="3" fill="#1A1A1A" />
        <ellipse cx="45" cy="110" rx="2" ry="3" fill="#1A1A1A" />

        {/* Air collar - adjustable ring */}
        <rect
          x="29"
          y="100"
          width="22"
          height="8"
          fill="#5A5A5A"
          rx="2"
        />
        <rect
          x="30"
          y="101"
          width="20"
          height="3"
          fill="#7A7A7A"
        />

        {/* Upper tube / barrel */}
        <rect
          x="33"
          y="60"
          width="14"
          height="28"
          fill={`url(#metal-${id})`}
        />

        {/* Burner head - top */}
        <rect
          x="30"
          y="55"
          width="20"
          height="8"
          fill="#5A5A5A"
          rx="1"
        />

        {/* Top opening */}
        <ellipse
          cx="40"
          cy="55"
          rx="9"
          ry="3"
          fill="#2A2A2A"
        />

        {/* Gas inlet tube */}
        <path
          d="M48 115 L55 115 L55 120 L65 120"
          fill="none"
          stroke="#5A5A5A"
          strokeWidth="4"
          strokeLinecap="round"
        />

        {/* Gas valve */}
        <circle cx="65" cy="120" r="5" fill="#4A4A4A" stroke="#6A6A6A" strokeWidth="1" />
        <rect x="63" y="117" width="4" height="6" fill="#3A3A3A" rx="1" />
      </g>

      {/* Flame when active */}
      {active && (
        <g filter={`url(#flame-glow-${id})`} style={flameAnimationStyle}>
          {/* Outer flame */}
          <ellipse
            cx="40"
            cy={55 - flameHeight.outer / 2}
            rx="12"
            ry={flameHeight.outer / 2}
            fill={`url(#flame-outer-${id})`}
          />

          {/* Inner flame - hottest */}
          <ellipse
            cx="40"
            cy={55 - flameHeight.inner / 2}
            rx="6"
            ry={flameHeight.inner / 2}
            fill={`url(#flame-inner-${id})`}
          />

          {/* Flame tip highlight */}
          <ellipse
            cx="40"
            cy={55 - flameHeight.outer + 5}
            rx="3"
            ry="5"
            fill="rgba(135, 206, 250, 0.4)"
          />
        </g>
      )}

      {/* Label */}
      <text
        x="40"
        y="138"
        fontSize="8"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Burner
      </text>
    </svg>
  )
}

export default BunsenBurner
